import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePublicClient, useSendTransaction } from "wagmi";
import { formatEther, parseEther, type Address } from "viem";
import { byId } from "@/lib/chains";
import { api } from "@/lib/api";
import { ExplorerLink } from "@/components/ExplorerLink";
import { CollectionHeader } from "./CollectionHeader";
import { CollectionMetadataPanel } from "./CollectionMetadataPanel";
import { useChainMismatch } from "@/hooks/useChainMismatch";
import type { Collection } from "./types";

type BadgeInfo = {
  signerAddress: Address;
  subsidized: boolean;
  relayerBalanceWei: string | null;
};

type CodeStats = {
  total: number;
  redeemed: number;
  remaining: number;
};

/**
 * Dashboard row for ERC721Badge — relayer wallet status, top-up, and
 * claim-link issuance. Subsidized badges expose top-up; self-paid skip it.
 */
export function BadgeRow({ c }: { c: Collection }) {
  const chain = byId(c.chainId);
  const qc = useQueryClient();
  const pc = usePublicClient({ chainId: c.chainId });
  const { sendTransactionAsync, isPending: sending } = useSendTransaction();
  const guard = useChainMismatch(c.chainId);

  const info = useQuery({
    queryKey: ["badge-info", c.chainId, c.contractAddress],
    queryFn: () => api.get<BadgeInfo>(`/api/badges/${c.chainId}/${c.contractAddress}`),
    refetchInterval: 30_000,
  });
  const stats = useQuery({
    queryKey: ["badge-stats", c.chainId, c.contractAddress],
    queryFn: () =>
      api.get<CodeStats>(`/api/badges/${c.chainId}/${c.contractAddress}/codes`, { auth: true }),
  });

  const [topupAmount, setTopupAmount] = useState("0.05");
  const [issueCount, setIssueCount] = useState(5);
  const [issued, setIssued] = useState<{ code: string; claimUrl: string }[] | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [showMeta, setShowMeta] = useState(false);

  const subsidized = !!c.subsidized;
  const balance = info.data?.relayerBalanceWei
    ? Number(formatEther(BigInt(info.data.relayerBalanceWei))).toFixed(4)
    : null;

  const topUp = async () => {
    if (!info.data?.signerAddress) return;
    setStatus(null);
    try {
      const hash = await sendTransactionAsync({
        to: info.data.signerAddress,
        value: parseEther(topupAmount || "0"),
        chainId: c.chainId,
      });
      setStatus("Top-up sent, confirming…");
      if (pc) await pc.waitForTransactionReceipt({ hash });
      setStatus(`Sent ${topupAmount} ${chain?.nativeCurrency.symbol ?? "ETH"}.`);
      void info.refetch();
    } catch (err) {
      setStatus(`Error: ${(err as Error).message}`);
    }
  };

  const issue = async () => {
    setStatus(null);
    setIssued(null);
    try {
      const resp = await api.post<{ codes: { code: string; claimId: string }[] }>(
        `/api/badges/${c.chainId}/${c.contractAddress}/codes`,
        { count: issueCount },
        { auth: true },
      );
      const base = typeof window !== "undefined" ? window.location.origin : "";
      const shortName = chain?.shortName ?? `c${c.chainId}`;
      setIssued(
        resp.codes.map((entry) => ({
          code: entry.code,
          claimUrl: `${base}/${shortName}/${c.contractAddress}/claim/${entry.code}`,
        })),
      );
      await qc.invalidateQueries({ queryKey: ["badge-stats", c.chainId, c.contractAddress] });
    } catch (err) {
      setStatus(`Error: ${(err as Error).message}`);
    }
  };

  const downloadCsv = () => {
    if (!issued) return;
    const csv = ["code,claim_url", ...issued.map((r) => `${r.code},${r.claimUrl}`)].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${c.symbol.toLowerCase()}-claim-links.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="card flex flex-col gap-4">
      <CollectionHeader c={c} />

      <div className="grid gap-3 text-xs md:grid-cols-3">
        <div className="rounded-md border border-border bg-muted/30 p-3">
          <div className="mb-1 text-muted-foreground">Mode</div>
          <div className="font-medium">
            {subsidized ? "Subsidized (you pay gas)" : "Self-paid (claimer pays gas)"}
          </div>
          {c.soulbound ? <div className="mt-1 text-muted-foreground">Soulbound</div> : null}
        </div>
        <div className="rounded-md border border-border bg-muted/30 p-3">
          <div className="mb-1 text-muted-foreground">Codes</div>
          {stats.isLoading ? (
            <div>—</div>
          ) : stats.isError ? (
            <div className="text-amber-700">unavailable</div>
          ) : (
            <div className="font-medium">
              {stats.data!.redeemed} / {stats.data!.total} redeemed
              <span className="ml-2 text-muted-foreground">({stats.data!.remaining} left)</span>
            </div>
          )}
        </div>
        <div className="rounded-md border border-border bg-muted/30 p-3">
          <div className="mb-1 text-muted-foreground">Relayer wallet</div>
          {info.isLoading ? (
            <div>—</div>
          ) : (
            <>
              <ExplorerLink
                chain={chain}
                kind="address"
                value={info.data?.signerAddress ?? "0x0"}
                className="text-xs"
              />
              <div className="mt-1 font-medium">
                {balance != null ? `${balance} ${chain?.nativeCurrency.symbol ?? ""}` : "balance unknown"}
              </div>
            </>
          )}
        </div>
      </div>

      {subsidized && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-muted-foreground">Top up relayer ({chain?.nativeCurrency.symbol})</span>
            <input
              className="input h-9 w-32"
              value={topupAmount}
              onChange={(e) => setTopupAmount(e.target.value)}
            />
          </label>
          <button
            className="btn-secondary h-9 px-3 text-xs"
            disabled={sending || !info.data?.signerAddress || guard.wrong}
            title={guard.tooltip}
            onClick={() => void topUp()}
          >
            {sending ? "Sending…" : "Top up"}
          </button>
        </div>
      )}
      {guard.wrong && (
        <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-200">
          <span className="font-medium">Wallet is on a different chain.</span>
          <button
            className="rounded border border-amber-500 px-2 py-0.5 text-[11px] font-semibold hover:bg-amber-500 hover:text-amber-50 disabled:opacity-50"
            onClick={guard.switchToTarget}
            disabled={guard.isSwitching}
          >
            {guard.isSwitching ? "Switching…" : `Switch to ${guard.targetName}`}
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-muted-foreground">Issue claim codes</span>
          <input
            type="number"
            min={1}
            max={500}
            className="input h-9 w-32"
            value={issueCount}
            onChange={(e) => setIssueCount(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
          />
        </label>
        <button className="btn-primary h-9 px-3 text-xs" onClick={() => void issue()}>
          Generate {issueCount} link{issueCount === 1 ? "" : "s"}
        </button>
        <button
          className="btn-ghost h-9 px-3 text-xs"
          onClick={() => setShowMeta((v) => !v)}
          aria-expanded={showMeta}
        >
          {showMeta ? "Hide metadata" : "Royalty + contractURI"}
        </button>
      </div>

      {status && <div className="text-xs text-muted-foreground">{status}</div>}

      {issued && (
        <div className="rounded-md border border-border bg-muted/30 p-3">
          <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
            <span>{issued.length} claim links generated — share or download. Codes are shown only once.</span>
            <button className="btn-ghost h-7 px-3 text-xs" onClick={downloadCsv}>Download CSV</button>
          </div>
          <ul className="max-h-64 space-y-1 overflow-y-auto text-xs">
            {issued.map((row) => (
              <li key={row.code} className="flex items-center gap-2">
                <code className="truncate text-foreground/90">{row.claimUrl}</code>
                <button
                  className="btn-ghost h-6 px-2 text-[10px]"
                  onClick={() => void navigator.clipboard.writeText(row.claimUrl)}
                >
                  copy
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showMeta && (
        <CollectionMetadataPanel
          contractAddress={c.contractAddress}
          chainId={c.chainId}
          kind="badge"
          name={c.name}
          description={c.description}
          imageCid={c.imageCid}
          bannerCid={c.bannerCid}
        />
      )}
    </div>
  );
}
